import { useRef, useState, useMemo, type ChangeEvent } from "react";
import { type DataTableRef, type DataTableOptions } from "@/components/tables";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import { useAccessCheck } from "@/hooks/useAccess";
import { exportToCSV } from "@/lib/utils/csvUtils";
import { apiClient } from "@/lib/api/client";
import { useThemeColors } from "@/hooks/useThemeColors";

import { useDisclosure } from "@chakra-ui/react";
type TabValue = "all" | "trash";


export default function useBasePageHook() {
  const colors = useThemeColors();
  const tableRef = useRef<DataTableRef>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showError, showSuccess, showWarning } = useToastNotification();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeTab, setActiveTab] = useState<TabValue>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [totalTrash, setTotalTrash] = useState(0);
  const [bulkAction, setBulkAction] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const access = useAccessCheck();

  const statusFilterOptions = useMemo(
    () => [
      { value: "online", label: "Online" },
      { value: "offline", label: "Offline" },
      { value: "idle", label: "Idle" },
    ],
    [],
  );


  const permissions = useMemo(
    () => ({
      // can_edit: access.can_edit,
      // can_delete: access.can_delete,
      // can_create: access.can_create,
      // can_view: access.can_view,
      // can_export: access.can_export,
      // can_import: access.can_import,
    }),
    [access],
  );



    const showSubSubCallback = useMemo(
      () => (all: number, trashed: number) => {
        setTotalRows(all);
        setTotalTrash(trashed);
      },
      [],
    );

    const handleTabChange = (index: number) => {
      setActiveTab(index === 0 ? "all" : "trash");
      setSearchQuery("");
      if (searchRef.current) {
        searchRef.current.value = "";
      }
      tableRef.current?.clearPage();
    };

    const handleSearch = () => {
      const query = searchRef.current?.value || "";
      setSearchQuery(query);
      tableRef.current?.applyFilter("search", query);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSearch();
      }
    };

    const handleBulkActionClick = () => {
      if (!bulkAction) {
        showWarning("Please select an action first");
        return;
      }
      const selectedIds = tableRef.current?.getSelectedRows() || [];
      if (selectedIds.length === 0) {
        showWarning("Please select at least one item");
        return;
      }

      if (bulkAction === "delete" && activeTab === "trash") {
        setModalMessage(
          "You are about to permanently delete these items from your site. This action cannot be undone.",
        );
      } else {
        setModalMessage("Are you sure to apply this change?");
      }
      onOpen();
    };

    const handleConfirmBulkAction = async () => {
      const selectedIds = tableRef.current?.getSelectedRows() || [];
      if (selectedIds.length === 0) return;

      setIsBulkProcessing(true);
      try {
        let url = "";
        let payload: any = { ids: selectedIds };
        let successMessage = "Action completed successfully";

        if (bulkAction === "restore") {
          url = "/user-management/roles/bulk/restore";
          successMessage = "Roles restored successfully";
        } else if (bulkAction === "delete") {
          url =
            activeTab === "trash"
              ? "/user-management/roles/bulk/force-delete"
              : "/user-management/roles/bulk/delete";
          successMessage =
            activeTab === "trash"
              ? "Roles permanently deleted successfully"
              : "Roles moved to trash successfully";
        }

        await apiClient.post(url, payload);
        showSuccess("Success", successMessage);
        tableRef.current?.reload();
        setBulkAction("");
        onClose();
      } catch (error: any) {
        showError(error);
      } finally {
        setIsBulkProcessing(false);
      }
    };

    const handleDownloadTemplate = async () => {
      try {
        const res = await apiClient.get(
          "/user-management/roles/template/download",
          { responseType: "blob" },
        );
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "roles_template.csv";
        a.click();
        URL.revokeObjectURL(url);
        showSuccess("Success", "Template downloaded");
      } catch (e: any) {
        showError(e);
      }
    };

    const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setIsImporting(true);
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await apiClient.post<{
          data: { imported: number; errors: string[] };
        }>("/user-management/roles/import", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const d = data?.data ?? { imported: 0, errors: [] };
        showSuccess("Success", `Imported ${d.imported} role(s)`);
        if (d.errors?.length) {
          showWarning(
            d.errors.slice(0, 3).join("; ") + (d.errors.length > 3 ? "..." : ""),
          );
        }
        tableRef.current?.reload();
      } catch (err: any) {
        showError(err);
      } finally {
        setIsImporting(false);
        e.target.value = "";
      }
    };

    const handleExportData = async () => {
      setIsExporting(true);
      try {
        await exportToCSV({
          endpoint: "/user-management/roles",
          selectedIds: [],
          activeTab,
          searchQuery: "",
          csvHeaders: [
            "ID",
            "Name",
            "Active",
            "Created At",
            "Updated At",
            "Deleted At",
          ],
          dataMapper: (role: any) => [
            role.id || "",
            role.name || "",
            role.active === "Active" ? "Yes" : "No",
            role.created_at || "",
            role.updated_at || "",
            role.deleted_at || "",
          ],
          filename: `roles_export_all${activeTab === "trash" ? "_trash" : ""}_${new Date().toISOString().split("T")[0]}.csv`,
          showError,
          showSuccess,
          showWarning,
        });
      } catch (error: any) {
        showError(error);
      } finally {
        setIsExporting(false);
      }
    };


   

    return {  
        colors,
        tableRef,
        searchRef,
        fileInputRef,
        showError, showSuccess, showWarning,
        isOpen, onOpen, onClose,
        activeTab,
        searchQuery,
        totalRows,
        totalTrash,
        bulkAction, setBulkAction,
        isExporting,
        isImporting,
        isBulkProcessing,
        modalMessage,
        statusFilterOptions,
        access,
        showSubSubCallback,
        handleTabChange,
        handleSearch,
        handleKeyDown,
        handleBulkActionClick,
        handleConfirmBulkAction ,
        handleDownloadTemplate,
        handleFileUpload,handleExportData,
        permissions,
    }
}
